// dependencies
import { addMonths, differenceInCalendarMonths, format, subDays, subMonths } from "date-fns";

import { IEngagementDefaultFilterValue } from "../../schema";

// returns the month labels for the x axis of the chart
export const getMonthsRange = (numberOfMonths: number) => {
	const dateRange: { label: string; value: string }[] = [];
	const today = new Date();

	let firstMonthSince = subMonths(today, numberOfMonths - 1);
	let monthsRange = numberOfMonths;

	// handle case where if we select 30 or 90 days filter,
	// check which month to start from
	// ex: if today is Jan 31, we want to start from Jan, not Dec.
	if (numberOfMonths === 2 || numberOfMonths === 4) {
		//check if 30 or 60 days prior to today is the same month
		const monthOfDaysPrior = subDays(today, (numberOfMonths - 1) * 30);
		firstMonthSince = monthOfDaysPrior;

		monthsRange = differenceInCalendarMonths(today, monthOfDaysPrior) + 1;
	}

	// get range of n months from today
	for (let i = 0; i < monthsRange; i++) {
		const formattedMonth = format(addMonths(firstMonthSince, i), "MMM");
		const formattedMonthAndYear = format(addMonths(firstMonthSince, i), "yyyy-MM");
		dateRange.push({ label: formattedMonth, value: formattedMonthAndYear });
	}

	return dateRange;
};

export const generateDateRangeLabels = (filterValue: IEngagementDefaultFilterValue, startDate: Date | null) => {
	switch (filterValue) {
		case "30":
			return getMonthsRange(2);
		case "90":
			return getMonthsRange(4);
		case "year":
			return getMonthsRange(13);
		default:
		case "all":
			if (!startDate) {
				return getMonthsRange(13);
			}
			return getMonthsRange(differenceInCalendarMonths(new Date(), startDate) + 1);
	}
};

// returns an array of point radiuses (to determine which point is selected)
export const getPointsRadiusArray = (
	dataSetIndex: number,
	dateRangeLabels: { label: string; value: string }[],
	hoveredDataSetIndex: number | null,
	dataValues: (string | null)[],
	indexOfSelectedMonth: number | null,
	indexOfSelectedCohort: number | null
) => {
	// handle the case where we want to display a single point
	// when there is only one available data point
	if (dataValues?.length <= 2) {
		if (dataValues.includes(null)) {
			if (indexOfSelectedMonth === 0) {
				return [5];
			}
			return [3];
		}
	}

	// on dataset hover, show all points.
	// selected point will have a radius of 5
	if (hoveredDataSetIndex !== null) {
		if (dataSetIndex === hoveredDataSetIndex) {
			return dateRangeLabels.map((label, index) => {
				if (index === indexOfSelectedMonth && indexOfSelectedCohort === dataSetIndex) {
					return 5;
				}
				return 3;
			});
		}
	}

	// when no dataset is hovered, show only selected point
	return dateRangeLabels.map((label, index) => {
		if (index === indexOfSelectedMonth && indexOfSelectedCohort === dataSetIndex) {
			return 5;
		}
		// sometimes maybe only one point exists, so we want to show it
		else if (dataValues?.filter((val) => val).length <= 1 && dataValues?.[index] !== null) {
			return 3;
		}
		return 0;
	});
};
